import { api } from '../client';

import {
  fetchCompleteBalancesByProductIds,
  type BalanceFilters,
  type BalanceWithAlert,
} from './balances';
import type { ProductPhotoVersion } from './barcodes';
import type { ListResponse, PaginationParams } from './common';
import type { StockMaterial, StockMaterialFilters, StockMaterialsResponse } from './materials';
import type { ProductsWithRecipesResponse, RecipeFlattenResponse } from './recipes';

/** Documents that wait for an admin, per section of the seller workspace. */
export const SELLER_APPROVAL_KINDS = [
  'transfers',
  'write_offs',
  'productions',
  'sale_reviews',
  'counts',
] as const;

export type SellerApprovalCounts = Record<(typeof SELLER_APPROVAL_KINDS)[number], number>;

/** What waits in all sections; the server may also count documents no section shows. */
export function sellerApprovalTotal(counts: SellerApprovalCounts): number {
  return SELLER_APPROVAL_KINDS.reduce((sum, kind) => sum + counts[kind], 0);
}

export type SellerTransferStatus =
  | 'requested'
  | 'assembling'
  | 'ready'
  | 'partially_assembled'
  | 'shipped'
  | 'received'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'deleted';

export type SellerReceiptDraftStatus = 'submitted' | 'approved' | 'rejected' | 'cancelled';
export type SellerTransferFlowType = 'request' | 'direct_transfer';
export type SellerWriteOffRequestStatus =
  | 'draft'
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'deleted';
export type SellerProductionRequestStatus =
  | 'draft'
  | 'requested'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'deleted';
export type SellerWriteOffReason =
  | 'spoilage'
  | 'expired'
  | 'damage'
  | 'theft'
  | 'other'
  | 'staff_meal'
  | 'promo_giveaway'
  | 'own_use'
  | 'tasting'
  | 'hot_leftover';
export type SellerResultState = 'unlinked' | 'confirmed' | 'draft' | 'deleted' | 'inconsistent';

export interface SellerResultHealth {
  result_state: SellerResultState;
  result_requires_attention: boolean;
}

export interface SellerLocationAccess {
  location_id: string;
  location_name: string;
  can_request_from: boolean;
  can_request_to: boolean;
  can_view_balance: boolean;
  can_assemble: boolean;
  can_receive: boolean;
  can_receive_supplier_receipt: boolean;
  supports_production: boolean;
  seller_mode: 'standard' | 'partner_order';
  /** The store keeps expiry dates: the «Сроки» tab (MD-0130). */
  expiry_enabled?: boolean;
  /** Sellers count products here: «Пересчёт» (MD-0132; the pilot is Wilanów). */
  count_enabled?: boolean;
}

export interface SellerAccessProfile {
  user_id: string;
  is_enabled: boolean;
  can_create_receipt_drafts: boolean;
  can_use_supplier_receiving?: boolean;
  locations: SellerLocationAccess[];
  /** The store the user last chose (kept on the server, the same on every device). */
  selected_location_id?: string | null;
}

export interface SellerWorkspaceContextResponse {
  request_id: string;
  profile: SellerAccessProfile;
}

export interface SellerProductionPlanItem {
  product_id: string;
  product_name: string;
  category_id?: string | null;
  category_name?: string | null;
  stock_unit_code: string;
  stock_is_weighted: boolean;
  ordered_quantity: string;
  ordered_stock_quantity: string;
  ordered_package_quantity: string | null;
  ordered_package_weight_kg: string | null;
  stock_quantity: string;
  difference_quantity: string;
  production_min_quantity: string | null;
  required_quantity: string;
  batch_output_min_quantity: string | null;
  batch_output_max_quantity: string | null;
  batch_count: number | null;
  planned_quantity_min: string | null;
  planned_quantity_max: string | null;
  planning_status: 'active' | 'paused' | 'unconfigured';
  destination_count: number;
  reason_codes: string[];
  shortage_ratio: string;
  priority_band: 'A' | 'B' | null;
  priority_rank: number | null;
  approximate_unit_weight_kg: string | null;
  stock_unit_weight_source: string | null;
  selected_batch_count: number;
  selected_quantity_min: string;
  selected_quantity_max: string;
  projected_stock_min_quantity: string;
  selection_rank: number | null;
  request_shortage_quantity: string;
  remaining_request_quantity: string;
  deferred_quantity: string;
  capacity_reason:
    | 'missing_unit_weight'
    | 'paused'
    | 'unconfigured'
    | 'capacity_request_shortage'
    | 'reserve_deferred'
    | 'covered';
}

export interface SellerProductionPlanStoreQuantity {
  unit_code: string;
  is_weighted: boolean;
  quantity: string;
  approximate_weight_kg: string | null;
}

export interface SellerProductionPlanStore {
  location_id: string;
  location_name: string;
  request_count: number;
  quantities: SellerProductionPlanStoreQuantity[];
}

export interface SellerProductionCapacity {
  limit_kg: string;
  planned_min_kg: string;
  planned_max_kg: string;
  remaining_kg: string;
  request_shortage_kg: string;
  remaining_request_kg: string;
  unknown_mass_count: number;
  deferred_count: number;
}

export interface SellerReplenishmentRow {
  location_id: string;
  location_name: string;
  product_id: string;
  product_name: string;
  category_name: string | null;
  history_days: number;
  full_history: boolean;
  stock_packages: string | null;
  incoming_packages: string | null;
  committed_packages: string | null;
  ordered_packages: string | null;
  recommended_packages: number | null;
  target_packages: number;
  daily_packages: string | null;
  coverage_days: string | null;
  status: 'ready' | 'limited_history' | 'missing_package_weight';
}

export interface SellerProductionPlanResponse {
  mode?: 'requests' | 'norms';
  recommendations?: {
    request_stores: SellerProductionPlanStore[];
    period_from: string;
    period_to: string;
    retained_request_lines: number;
    rows: SellerReplenishmentRow[];
  } | null;
  request_id: string;
  location_id: string;
  location_name: string;
  generated_at: string;
  target_date: string;
  capacity: SellerProductionCapacity;
  stores: SellerProductionPlanStore[];
  items: SellerProductionPlanItem[];
}

export interface UpdateSellerLocationAccess {
  location_id: string;
  can_request_from: boolean;
  can_request_to: boolean;
  can_view_balance: boolean;
  can_assemble: boolean;
  can_receive: boolean;
}

export interface UpdateSellerAccessProfile {
  is_enabled: boolean;
  can_create_receipt_drafts: boolean;
  locations: UpdateSellerLocationAccess[];
}

/** Per-category counts over one location's current balances. */
export interface SellerBalanceCategorySummaryItem {
  /** Null for products without a category. */
  category_id: string | null;
  category_name: string | null;
  total: number;
  non_zero: number;
  /** negative + zero + low alert statuses */
  issues: number;
}

export interface SellerBalanceCategorySummaryResponse {
  request_id: string;
  items: SellerBalanceCategorySummaryItem[];
}

/** Products a source no longer offers for ordering: not made there and none left. */
export interface SellerOrderExclusionsResponse {
  request_id: string;
  location_id: string;
  product_ids: string[];
}

export interface SellerProduct {
  stock_unit_weight_kg?: string | null;
  id: string;
  name: string;
  unit_code: string;
  is_weighted: boolean;
  category_id?: string | null;
  category_name?: string | null;
  request_package_weight_kg?: string | null;
  /** Current pack photos, for the thumbnail beside the name. */
  photos?: ProductPhotoVersion[];
}

export interface SellerProductFilters extends PaginationParams {
  search?: string;
  location_id?: string;
  include_raw_ingredients?: boolean;
}

const SELLER_PRODUCT_PAGE_SIZE = 1000;
const SELLER_MATERIAL_PAGE_SIZE = 200;
export const SELLER_BALANCE_PRODUCT_IDS_LIMIT = 100;

export async function collectAllOffsetPages<T, TResponse extends ListResponse<T>>(
  fetchPage: (offset: number, limit: number) => Promise<TResponse>,
  pageSize: number,
): Promise<TResponse> {
  const firstPage = await fetchPage(0, pageSize);
  const items = [...firstPage.items];
  let page = firstPage;

  while (page.paging.has_more) {
    const nextOffset = page.paging.offset + page.items.length;
    if (nextOffset <= page.paging.offset) {
      throw new Error('Catalog pagination did not advance');
    }
    page = await fetchPage(nextOffset, pageSize);
    items.push(...page.items);
  }

  return {
    ...firstPage,
    request_id: page.request_id || firstPage.request_id,
    items,
    paging: {
      limit: items.length,
      offset: 0,
      total: Math.max(page.paging.total, items.length),
      has_more: false,
    },
  };
}

/** Where a transfer line entered the document (MD-0051). */
export type SellerTransferLineOrigin = 'requested' | 'assembly_added' | 'receipt_added';

/**
 * Server-derived per-line discrepancy classification (MD-0051). `null` is clean.
 * - `shortage`: shipped but received 0 with no substitute.
 * - `mispick`: received 0 and another line substitutes it.
 * - `added_at_assembly` / `added_at_receipt`: line added during that stage.
 * - `over`: received more than assembled.
 */
export type SellerTransferLineDiscrepancy =
  | 'shortage'
  | 'mispick'
  | 'added_at_assembly'
  | 'added_at_receipt'
  | 'over';

export interface SellerTransferRequestItem {
  id: string;
  product_id: string;
  product_name?: string | null;
  unit_code: string;
  is_weighted?: boolean | null;
  requested_quantity: string;
  requested_package_quantity?: string | null;
  requested_package_weight_kg?: string | null;
  assembled_quantity?: string | null;
  received_quantity?: string | null;
  /** Quantity the admin signed off; this is what actually moves on approval. */
  approved_quantity?: string | null;
  assembly_note?: string | null;
  /** Stable per-request line number; pairs with `substitutes_line_no` (MD-0051). */
  line_no?: number | null;
  /** Where this line entered the document (MD-0051). */
  origin?: SellerTransferLineOrigin | null;
  /** When set, this line replaces the request line with that `line_no` (пересорт). */
  substitutes_line_no?: number | null;
  /** Server-derived discrepancy classification; `null`/absent is clean (MD-0051). */
  discrepancy?: SellerTransferLineDiscrepancy | null;
  /**
   * Balance snapshots on the detail response (source decreases, destination
   * increases); absent on list responses. For an approved request these are the
   * real posted ledger numbers. For a shipped/received request awaiting approval
   * they are an *expected* delta (current balance ± the quantity approval would
   * post) and `balance_projected` is true (MD-0050: stock posts on approval).
   */
  from_balance_before?: string | null;
  from_balance_after?: string | null;
  to_balance_before?: string | null;
  to_balance_after?: string | null;
  /** True when the balance snapshots are an expected projection, not posted. */
  balance_projected?: boolean | null;
}

/** Per-request discrepancy counts driving the exception-based admin queue (MD-0051). */
export interface SellerTransferDiscrepancySummary {
  mispick: number;
  shortage: number;
  added: number;
  over: number;
}

export interface SellerTransferRequest extends SellerResultHealth {
  id: string;
  from_location_id: string;
  from_location_name?: string | null;
  to_location_id: string;
  to_location_name?: string | null;
  flow_type: SellerTransferFlowType;
  status: SellerTransferStatus;
  requested_by: string;
  requested_by_name?: string | null;
  /** User who collected + dispatched the request. */
  shipped_by?: string | null;
  /** Who collected + dispatched (assemble+ship is one action). */
  shipped_by_name?: string | null;
  /** Who confirmed receipt at the destination store. */
  received_by_name?: string | null;
  /** Admin who approved the request into a stock transfer. */
  approved_by_name?: string | null;
  /** Admin who rejected the received request. */
  rejected_by_name?: string | null;
  deleted_by?: string | null;
  deleted_by_name?: string | null;
  transfer_id?: string | null;
  requested_at: string;
  assembling_at?: string | null;
  assembled_at?: string | null;
  shipped_at?: string | null;
  received_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  cancelled_at?: string | null;
  deleted_at?: string | null;
  delete_reason?: string | null;
  restored_at?: string | null;
  restored_by?: string | null;
  notes?: string | null;
  assembly_notes?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
  items: SellerTransferRequestItem[];
  /**
   * False in a list asked with `include_items: false`: `items` is empty there and
   * the counters below stand in for the lines until the detail request brings them.
   */
  items_included?: boolean;
  /** How many lines the request has. */
  items_count?: number | null;
  /** Lines received with a quantity other than the shipped one («отгружено ≠ получено»). */
  receiving_discrepancy_count?: number | null;
  /** Per-request discrepancy counts (MD-0051); present when the server emits them. */
  discrepancy_summary?: SellerTransferDiscrepancySummary | null;
  /** True when any line carries a пересорт/недостача/добавлено/over discrepancy (MD-0051). */
  has_discrepancy?: boolean | null;
  /** MD-0109: who approved (null on legacy approvals = admin). */
  approval_source?: SellerApprovalSource | null;
  approval_policy_version?: number | null;
  /** Why the store policy left a clean receipt for the admin. */
  auto_approval_error?: string | null;
  updated_at?: string | null;
}

export interface SellerReceiptDraftItem {
  id: string;
  product_id: string;
  product_name?: string | null;
  unit_code: string;
  is_weighted?: boolean | null;
  quantity: string;
  admin_unit_price?: string | null;
  admin_vat_rate?: string | null;
  admin_total_price?: string | null;
}

export interface SellerReceiptDraftAttachment {
  id: string;
  original_filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  created_at: string;
}

export interface SellerReceiptDraft extends SellerResultHealth {
  id: string;
  location_id: string;
  location_name?: string | null;
  supplier_id: string;
  supplier_name?: string | null;
  status: SellerReceiptDraftStatus;
  created_by: string;
  receipt_id?: string | null;
  received_at: string;
  notes?: string | null;
  admin_notes?: string | null;
  reject_reason?: string | null;
  created_at: string;
  items: SellerReceiptDraftItem[];
  attachments: SellerReceiptDraftAttachment[];
}

export interface SellerWriteOffRequestItem {
  id: string;
  line_no?: number | null;
  product_id: string;
  product_name?: string | null;
  unit_code: string;
  is_weighted?: boolean | null;
  requested_quantity: string;
  approved_quantity?: string | null;
  /** The admin's list only: latest unit cost, the store's till price, and the line at each. */
  unit_cost?: string | null;
  cost_amount?: string | null;
  unit_price?: string | null;
  retail_amount?: string | null;
}

export interface SellerWriteOffRequest extends SellerResultHealth {
  id: string;
  location_id: string;
  location_name?: string | null;
  status: SellerWriteOffRequestStatus;
  reason: SellerWriteOffReason;
  requested_by: string;
  requested_by_name?: string | null;
  approved_by?: string | null;
  approved_by_name?: string | null;
  rejected_by?: string | null;
  rejected_by_name?: string | null;
  cancelled_by?: string | null;
  cancelled_by_name?: string | null;
  deleted_by?: string | null;
  deleted_by_name?: string | null;
  restored_by?: string | null;
  restored_by_name?: string | null;
  write_off_id?: string | null;
  requested_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  cancelled_at?: string | null;
  deleted_at?: string | null;
  restored_at?: string | null;
  notes?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
  delete_reason?: string | null;
  created_at: string;
  updated_at: string;
  items: SellerWriteOffRequestItem[];
  /** MD-0109: see SellerTransferRequest; approval_value is the value the policy evaluated. */
  approval_source?: SellerApprovalSource | null;
  approval_policy_version?: number | null;
  approval_value?: string | null;
  auto_approval_error?: string | null;
  /** The admin's list only: the document at cost and at till prices; a sum leaves out
   * the lines counted in *_missing_lines and is empty when no line is known. */
  cost_amount?: string | null;
  cost_missing_lines?: number | null;
  retail_amount?: string | null;
  retail_missing_lines?: number | null;
}

export type SellerApprovalSource = 'admin' | 'auto_policy';

/** A store's seller approval policy (MD-0109); version 0 = everything goes to the admin. */
export interface SellerApprovalPolicy {
  location_id: string;
  version: number;
  receipt_auto_approve: boolean;
  receipt_tolerance_percent: string;
  write_off_auto_limit: string | null;
  write_off_daily_limit: string | null;
  updated_at: string | null;
  updated_by_name: string | null;
}

export interface SellerApprovalPolicyUpdate {
  expected_version: number;
  receipt_auto_approve: boolean;
  receipt_tolerance_percent: string;
  write_off_auto_limit: string | null;
  write_off_daily_limit: string | null;
}

export interface SellerProductionActualConsumption {
  ingredient_product_id: string;
  actual_quantity: string;
  unit_code: string;
}

export interface SellerProductionConsumptionLine {
  implicit_quantity?: string;
  actual_quantity?: string;
  is_override?: boolean;
  ingredient_product_id: string;
  product_name?: string | null;
  quantity_per_unit?: string | null;
  projected_quantity: string;
  unit_code?: string | null;
}

export interface SellerProductionRequestItem {
  actual_weight_kg?: string | null;
  category_id?: string | null;
  category_name?: string | null;
  id: string;
  line_no?: number | null;
  product_id: string;
  product_name?: string | null;
  recipe_id: string;
  unit_code: string;
  is_weighted?: boolean | null;
  requested_quantity: string;
  approved_quantity?: string | null;
  consumption_snapshot?: SellerProductionConsumptionLine[] | null;
}

export interface SellerProductionBatch {
  id: string;
  ready_at: string;
  created_at: string;
  notes: string | null;
  items: SellerProductionRequestItem[];
}

export interface SellerProductionRequest extends SellerResultHealth {
  batches: SellerProductionBatch[];
  ready_at: string;
  id: string;
  location_id: string;
  location_name?: string | null;
  status: SellerProductionRequestStatus;
  requested_by: string;
  requested_by_name?: string | null;
  approved_by?: string | null;
  approved_by_name?: string | null;
  rejected_by?: string | null;
  rejected_by_name?: string | null;
  cancelled_by?: string | null;
  cancelled_by_name?: string | null;
  deleted_by?: string | null;
  deleted_by_name?: string | null;
  restored_by?: string | null;
  restored_by_name?: string | null;
  production_id?: string | null;
  requested_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  cancelled_at?: string | null;
  deleted_at?: string | null;
  restored_at?: string | null;
  notes?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
  delete_reason?: string | null;
  created_at: string;
  updated_at: string;
  items: SellerProductionRequestItem[];
}

export interface SellerTransferRequestFilters extends PaginationParams {
  status?: string;
  mine?: boolean;
  updated_from?: string;
  /** Only requests sent from or to this location. */
  location_id?: string;
  /** `false`: headers and line counters only (see `items_included`). Default: with lines. */
  include_items?: boolean;
}

export interface SellerReceiptDraftFilters extends PaginationParams {
  status?: string;
  mine?: boolean;
}

export interface SellerWriteOffRequestFilters extends PaginationParams {
  status?: string;
  location_id?: string;
  mine?: boolean;
  updated_from?: string;
}

export interface SellerProductionRequestFilters extends PaginationParams {
  status?: string;
  location_id?: string;
  mine?: boolean;
  updated_from?: string;
}

export interface CreateSellerTransferRequest {
  from_location_id: string;
  to_location_id: string;
  notes?: string;
  items: Array<{
    product_id: string;
    quantity: string;
    unit_code: string;
    input_unit?: 'stock' | 'package';
  }>;
  suggestion?: { revision: string; reasons: Record<string, string> };
}

export interface UpdateSellerTransferRequest {
  notes?: string;
  items: Array<{
    product_id: string;
    quantity: string;
    unit_code: string;
    input_unit?: 'stock' | 'package';
  }>;
}

export interface CreateSellerDirectTransfer {
  from_location_id: string;
  to_location_id: string;
  notes?: string;
  items: Array<{ product_id: string; quantity: string; unit_code: string }>;
}

export interface AssembleSellerTransferRequest {
  assembly_notes?: string;
  items: Array<{ product_id: string; assembled_quantity: string; assembly_note?: string }>;
  /**
   * Products added during assembly (MD-0051). Pure add → omit `substitutes_line_no`;
   * пересорт → set it to the zeroed line's `line_no` (that line goes in `items` at 0).
   */
  added_items?: Array<{
    product_id: string;
    unit_code: string;
    assembled_quantity: string;
    substitutes_line_no?: number | null;
    assembly_note?: string;
  }>;
}

export interface ReceiveSellerTransferRequest {
  /** Optional per-line received quantities; omitted lines default to shipped. */
  items?: Array<{ product_id: string; received_quantity: string }>;
  /**
   * Products added during receipt (MD-0051). Pure add → omit `substitutes_line_no`;
   * пересорт → set it to the zeroed line's `line_no` (that line goes in `items` at 0).
   */
  added_items?: Array<{
    product_id: string;
    unit_code: string;
    received_quantity: string;
    substitutes_line_no?: number | null;
  }>;
}

export interface ApproveSellerTransferRequest {
  /** Optional per-line approved quantities; omitted lines default to received. */
  items?: Array<{ product_id: string; approved_quantity: string }>;
  /** Movement date/time (ISO); defaults to the request's shipment time. */
  transferred_at?: string;
}

export interface CreateSellerWriteOffRequest {
  location_id: string;
  reason: SellerWriteOffReason;
  notes?: string;
  initial_status?: 'draft' | 'requested';
  items: Array<{ product_id: string; quantity: string; unit_code: string }>;
}

export interface UpdateSellerWriteOffRequest {
  location_id?: string;
  reason?: SellerWriteOffReason;
  notes?: string | null;
  items?: Array<{ product_id: string; quantity: string; unit_code: string }>;
}

export interface ApproveSellerWriteOffRequest {
  expected_updated_at: string;
  items?: Array<{ product_id: string; approved_quantity: string }>;
  write_off_at?: string;
}

export interface CreateSellerProductionRequest {
  ready_at?: string;
  location_id: string;
  notes?: string;
  initial_status?: 'draft' | 'requested';
  items: Array<{
    product_id: string;
    quantity: string;
    actual_weight_kg?: string | null;
    unit_code: string;
    recipe_id?: string;
    consumption_items?: SellerProductionActualConsumption[];
  }>;
}

export interface UpdateSellerProductionRequest {
  ready_at?: string;
  expected_updated_at: string;
  correction_reason?: string;
  location_id?: string;
  notes?: string | null;
  items?: Array<{
    product_id: string;
    quantity: string;
    actual_weight_kg?: string | null;
    unit_code: string;
    recipe_id?: string;
    consumption_items?: SellerProductionActualConsumption[];
  }>;
}

export interface ApproveSellerProductionRequest {
  location_id?: string;
  expected_updated_at: string;
  items?: Array<{
    product_id: string;
    approved_quantity: string;
    consumption_items?: SellerProductionActualConsumption[];
  }>;
  production_date: string;
}

export interface CreateSellerReceiptDraft {
  location_id: string;
  supplier_id: string;
  received_at?: string;
  notes?: string;
  items: Array<{ product_id: string; quantity: string; unit_code: string }>;
}

export interface ApproveSellerReceiptDraft {
  admin_notes?: string;
  items: Array<{
    product_id: string;
    admin_unit_price: string;
    admin_vat_rate: string;
    admin_total_price: string;
  }>;
}

export const createStockSellerWorkspaceApi = () => ({
  context: (): Promise<SellerWorkspaceContextResponse> =>
    api.webAdmin.get('/stock/seller-workspace/context'),

  setCurrentLocation: (
    locationId: string,
  ): Promise<{ request_id: string; selected_location_id: string }> =>
    api.webAdmin.put('/stock/seller-workspace/current-location', { location_id: locationId }),

  getUserAccess: (userId: string): Promise<SellerWorkspaceContextResponse> =>
    api.webAdmin.get(`/stock/seller-workspace/admin/users/${userId}/access`),

  getApprovalPolicy: (
    locationId: string,
  ): Promise<{ request_id: string; policy: SellerApprovalPolicy }> =>
    api.webAdmin.get(`/stock/seller-workspace/approval-policies/${locationId}`),

  updateApprovalPolicy: (
    locationId: string,
    data: SellerApprovalPolicyUpdate,
  ): Promise<{ request_id: string; policy: SellerApprovalPolicy }> =>
    api.webAdmin.put(`/stock/seller-workspace/approval-policies/${locationId}`, data),

  updateUserAccess: (
    userId: string,
    data: UpdateSellerAccessProfile,
  ): Promise<SellerWorkspaceContextResponse> =>
    api.webAdmin.put(`/stock/seller-workspace/admin/users/${userId}/access`, data),

  listBalances: (filters: BalanceFilters): Promise<ListResponse<BalanceWithAlert>> =>
    fetchCompleteBalancesByProductIds(filters, SELLER_BALANCE_PRODUCT_IDS_LIMIT, (chunkFilters) =>
      api.webAdmin.get('/stock/seller-workspace/balances', { params: chunkFilters }),
    ),

  listBalanceCategories: (filters: {
    location_id: string;
  }): Promise<SellerBalanceCategorySummaryResponse> =>
    api.webAdmin.get('/stock/seller-workspace/balances/categories', { params: filters }),

  listOrderExclusions: (filters: { location_id: string }): Promise<SellerOrderExclusionsResponse> =>
    api.webAdmin.get('/stock/seller-workspace/order-exclusions', { params: filters }),

  listMaterials: (filters: StockMaterialFilters = {}): Promise<StockMaterialsResponse> =>
    api.webAdmin.get('/stock/seller-workspace/materials', { params: filters }),

  listAllMaterials: (filters: StockMaterialFilters = {}): Promise<StockMaterialsResponse> =>
    collectAllOffsetPages<StockMaterial, StockMaterialsResponse>(
      (offset, limit) =>
        api.webAdmin.get('/stock/seller-workspace/materials', {
          params: { ...filters, offset, limit },
        }),
      SELLER_MATERIAL_PAGE_SIZE,
    ),

  listProducts: (filters: SellerProductFilters = {}): Promise<ListResponse<SellerProduct>> =>
    api.webAdmin.get('/stock/seller-workspace/products', { params: filters }),

  listAllProducts: (filters: SellerProductFilters = {}): Promise<ListResponse<SellerProduct>> =>
    collectAllOffsetPages<SellerProduct, ListResponse<SellerProduct>>(
      (offset, limit) =>
        api.webAdmin.get('/stock/seller-workspace/products', {
          params: { ...filters, offset, limit },
        }),
      SELLER_PRODUCT_PAGE_SIZE,
    ),

  productsWithRecipes: (): Promise<ProductsWithRecipesResponse> =>
    api.webAdmin.get('/stock/seller-workspace/products-with-recipes'),

  flattenActiveRecipePerUnit: (productId: string): Promise<RecipeFlattenResponse> =>
    api.webAdmin.get(`/stock/seller-workspace/recipes/flatten/${productId}`),

  approvalCounts: (locationId?: string): Promise<SellerApprovalCounts> =>
    api.webAdmin.get('/stock/seller-workspace/approval-counts', {
      params: { location_id: locationId },
    }),

  listTransferRequests: (
    filters: SellerTransferRequestFilters = {},
  ): Promise<ListResponse<SellerTransferRequest>> =>
    api.webAdmin.get('/stock/seller-workspace/transfer-requests', { params: filters }),

  getTransferRequest: (id: string): Promise<SellerTransferRequest> =>
    api.webAdmin.get(`/stock/seller-workspace/transfer-requests/${id}`),

  createTransferRequest: (
    data: CreateSellerTransferRequest,
    idempotencyKey?: string,
  ): Promise<SellerTransferRequest> =>
    api.webAdmin.post(
      '/stock/seller-workspace/transfer-requests',
      data,
      idempotencyKey ? { idempotencyKey } : undefined,
    ),

  createDirectTransfer: (
    data: CreateSellerDirectTransfer,
    idempotencyKey?: string,
  ): Promise<SellerTransferRequest> =>
    api.webAdmin.post(
      '/stock/seller-workspace/transfer-requests/direct',
      data,
      idempotencyKey ? { idempotencyKey } : undefined,
    ),

  updateTransferRequest: (
    id: string,
    data: UpdateSellerTransferRequest,
  ): Promise<SellerTransferRequest> =>
    api.webAdmin.patch(`/stock/seller-workspace/transfer-requests/${id}`, data),

  startAssembly: (id: string): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/start-assembly`, {}),

  assemble: (id: string, data: AssembleSellerTransferRequest): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/assemble`, data),

  ship: (id: string): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/ship`, {}),

  receive: (id: string, data: ReceiveSellerTransferRequest = {}): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/receive`, data),

  approveTransferRequest: (
    id: string,
    data: ApproveSellerTransferRequest = {},
  ): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/approve`, data),

  rejectTransferRequest: (id: string, reason?: string): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/reject`, { reason }),

  cancelTransferRequest: (id: string, reason?: string): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/cancel`, { reason }),

  deleteTransferRequest: (id: string): Promise<SellerTransferRequest> =>
    api.webAdmin.delete(`/stock/seller-workspace/transfer-requests/${id}`),

  restoreTransferRequest: (id: string): Promise<SellerTransferRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/transfer-requests/${id}/restore`, {}),

  listWriteOffRequests: (
    filters: SellerWriteOffRequestFilters = {},
  ): Promise<ListResponse<SellerWriteOffRequest>> =>
    api.webAdmin.get('/stock/seller-workspace/write-off-requests', { params: filters }),

  getWriteOffRequest: (id: string): Promise<SellerWriteOffRequest> =>
    api.webAdmin.get(`/stock/seller-workspace/write-off-requests/${id}`),

  createWriteOffRequest: (
    data: CreateSellerWriteOffRequest,
    idempotencyKey?: string,
  ): Promise<SellerWriteOffRequest> =>
    api.webAdmin.post(
      '/stock/seller-workspace/write-off-requests',
      data,
      idempotencyKey ? { idempotencyKey } : undefined,
    ),

  updateWriteOffRequest: (
    id: string,
    data: UpdateSellerWriteOffRequest,
  ): Promise<SellerWriteOffRequest> =>
    api.webAdmin.patch(`/stock/seller-workspace/write-off-requests/${id}`, data),

  submitWriteOffRequest: (id: string): Promise<SellerWriteOffRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/write-off-requests/${id}/submit`, {}),

  approveWriteOffRequest: (
    id: string,
    data: ApproveSellerWriteOffRequest,
  ): Promise<SellerWriteOffRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/write-off-requests/${id}/approve`, data),

  rejectWriteOffRequest: (id: string, reason?: string): Promise<SellerWriteOffRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/write-off-requests/${id}/reject`, { reason }),

  cancelWriteOffRequest: (id: string, reason?: string): Promise<SellerWriteOffRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/write-off-requests/${id}/cancel`, { reason }),

  deleteWriteOffRequest: (id: string): Promise<SellerWriteOffRequest> =>
    api.webAdmin.delete(`/stock/seller-workspace/write-off-requests/${id}`),

  restoreWriteOffRequest: (id: string): Promise<SellerWriteOffRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/write-off-requests/${id}/restore`, {}),

  getProductionDay: (locationId: string, businessDate: string): Promise<SellerProductionDay> =>
    api.webAdmin.get('/stock/seller-workspace/production-day', {
      params: { location_id: locationId, business_date: businessDate },
    }),
  setProductionDayStatus: (
    data: {
      location_id: string;
      business_date: string;
      closed: boolean;
      expected_revision: number;
    },
    idempotencyKey: string,
  ): Promise<SellerProductionDay> =>
    api.webAdmin.post(
      '/stock/seller-workspace/production-day/status',
      data,
      idempotencyKey ? { idempotencyKey } : undefined,
    ),
  listProductionRequests: (
    filters: SellerProductionRequestFilters = {},
  ): Promise<ListResponse<SellerProductionRequest>> =>
    api.webAdmin.get('/stock/seller-workspace/production-requests', { params: filters }),

  getProductionPlan: (
    locationId: string,
    capacityKg = 450,
    mode: 'requests' | 'norms' = 'requests',
  ): Promise<SellerProductionPlanResponse> =>
    api.webAdmin.get('/stock/seller-workspace/production-plan', {
      params: { location_id: locationId, capacity_kg: capacityKg, mode },
    }),

  productionPlanPdfUrl: (
    locationId: string,
    capacityKg = 450,
    mode: 'requests' | 'norms' = 'requests',
  ): string => {
    const params = new URLSearchParams({
      location_id: locationId,
      capacity_kg: String(capacityKg),
      mode,
    });
    return `/api/web-admin/stock/seller-workspace/production-plan.pdf?${params.toString()}`;
  },

  getProductionRequest: (id: string): Promise<SellerProductionRequest> =>
    api.webAdmin.get(`/stock/seller-workspace/production-requests/${id}`),

  createProductionRequest: (
    data: CreateSellerProductionRequest,
    idempotencyKey?: string,
  ): Promise<SellerProductionRequest> =>
    api.webAdmin.post(
      '/stock/seller-workspace/production-requests',
      data,
      idempotencyKey ? { idempotencyKey } : undefined,
    ),

  updateProductionRequest: (
    id: string,
    data: UpdateSellerProductionRequest,
    idempotencyKey?: string,
  ): Promise<SellerProductionRequest> =>
    api.webAdmin.patch(
      `/stock/seller-workspace/production-requests/${id}`,
      data,
      idempotencyKey ? { idempotencyKey } : undefined,
    ),

  submitProductionRequest: (id: string): Promise<SellerProductionRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/production-requests/${id}/submit`, {}),

  approveProductionRequest: (
    id: string,
    data: ApproveSellerProductionRequest,
  ): Promise<SellerProductionRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/production-requests/${id}/approve`, data),

  rejectProductionRequest: (id: string, reason?: string): Promise<SellerProductionRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/production-requests/${id}/reject`, { reason }),

  cancelProductionRequest: (
    id: string,
    reason: string | undefined,
    expectedUpdatedAt: string,
  ): Promise<SellerProductionRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/production-requests/${id}/cancel`, {
      reason,
      expected_updated_at: expectedUpdatedAt,
    }),

  deleteProductionRequest: (id: string): Promise<SellerProductionRequest> =>
    api.webAdmin.delete(`/stock/seller-workspace/production-requests/${id}`),

  restoreProductionRequest: (id: string): Promise<SellerProductionRequest> =>
    api.webAdmin.post(`/stock/seller-workspace/production-requests/${id}/restore`, {}),

  listReceiptDrafts: (
    filters: SellerReceiptDraftFilters = {},
  ): Promise<ListResponse<SellerReceiptDraft>> =>
    api.webAdmin.get('/stock/seller-workspace/receipt-drafts', { params: filters }),

  createReceiptDraft: (
    data: CreateSellerReceiptDraft,
    idempotencyKey?: string,
  ): Promise<SellerReceiptDraft> =>
    api.webAdmin.post(
      '/stock/seller-workspace/receipt-drafts',
      data,
      idempotencyKey ? { idempotencyKey } : undefined,
    ),

  uploadReceiptDraftAttachment: (id: string, file: File): Promise<SellerReceiptDraft> => {
    const form = new FormData();
    form.append('file', file);
    return api.webAdmin.post(`/stock/seller-workspace/receipt-drafts/${id}/attachments`, form);
  },

  approveReceiptDraft: (id: string, data: ApproveSellerReceiptDraft): Promise<SellerReceiptDraft> =>
    api.webAdmin.post(`/stock/seller-workspace/receipt-drafts/${id}/approve`, data),

  rejectReceiptDraft: (id: string, reason?: string): Promise<SellerReceiptDraft> =>
    api.webAdmin.post(`/stock/seller-workspace/receipt-drafts/${id}/reject`, { reason }),
});

export interface ProductionSnapshot {
  ready_at: string;
  notes: string | null;
  items: Array<{
    product_id: string;
    quantity: string;
    actual_weight_kg?: string | null;
    unit_code: string;
    consumption_snapshot?: SellerProductionConsumptionLine[] | null;
  }>;
}
export interface SellerProductionDay {
  location_id: string;
  business_date: string;
  closed_at: string | null;
  revision: number;
  items: SellerProductionRequest[];
  events: Array<{
    id: string;
    request_id: string | null;
    kind: string;
    reason: string | null;
    actor_name: string;
    happened_at: string;
    before_snapshot: ProductionSnapshot | null;
    after_snapshot: ProductionSnapshot | null;
  }>;
}
